import { ColDef } from 'ag-grid-community';
import { parseISO } from 'date-fns';

import { normalizeTimestamp } from '@/shared/utils/format';

type TTimestamp = string | number | null | undefined;

// Mirrors formatLocalTime, so anything the cell renders as "Invalid date" sorts as missing.
const toTime = (value: TTimestamp): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const time = parseISO(normalizeTimestamp(value)).getTime();

  return Number.isNaN(time) ? null : time;
};

// Date columns mostly display a localised string, which sorts alphabetically and not
// chronologically. Pass getTimestamp when the cell value is not the raw timestamp.
export const getDateComparator =
  <T>(
    getTimestamp?: (data: T) => TTimestamp,
  ): NonNullable<ColDef<T>['comparator']> =>
  (valueA, valueB, nodeA, nodeB, isDescending) => {
    const read = (value: unknown, data?: T | null): TTimestamp => {
      if (!getTimestamp) {
        return value as TTimestamp;
      }

      return data ? getTimestamp(data) : null;
    };

    const timeA = toTime(read(valueA, nodeA?.data));
    const timeB = toTime(read(valueB, nodeB?.data));

    if (timeA === null && timeB === null) {
      return 0;
    }

    // Rows without a date stay at the bottom in both directions.
    if (timeA === null) {
      return isDescending ? -1 : 1;
    }

    if (timeB === null) {
      return isDescending ? 1 : -1;
    }

    return timeA - timeB;
  };
